import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, Plus, Lock, LogOut, KeyRound, Pencil, Trash2, X, Check,
  FileText, FilePlus, Files, BarChart3, RefreshCw, ArrowUp, ArrowDown, Save,
  GripVertical, ChevronDown, ChevronUp,
} from "lucide-react";
import {
  listScenarios,
  createScenario,
  updateScenario,
  deleteScenario,
  adminLogin,
  adminMe,
  adminChangePassword,
  clearAdminToken,
  getAdminToken,
  listReferences,
  uploadReference,
  deleteReference,
  updateReference,
  reorderReferences,
  sortReferencesAlpha,
  referenceFileUrl,
} from "../lib/api";
import { toast, Toaster } from "sonner";
import { useFeatureTimer, getAnalyticsSummary } from "../lib/analytics";
import PdfViewer from "../components/PdfViewer";
import { useConfirm } from "../components/ConfirmProvider";

const CATEGORIES = ["shock", "cardiac", "respiratory", "neuro", "metabolic", "trauma"];

const emptyForm = {
  title: "",
  category: "shock",
  age: 50,
  gender: "M",
  context: "",
  HR: "",
  BP: "",
  MAP: "",
  RR: "",
  SpO2: "",
  Temp: "",
  critical_data: "",
};

function scenarioToForm(s) {
  const v = s.vitals || {};
  return {
    title: s.title,
    category: s.category,
    age: s.age,
    gender: s.gender,
    context: s.context,
    HR: v.HR || "",
    BP: v.BP || "",
    MAP: v.MAP || "",
    RR: v.RR || "",
    SpO2: v.SpO2 || "",
    Temp: v.Temp || "",
    critical_data: (s.critical_data || []).join("\n"),
  };
}

function formToPayload(form) {
  const vitals = {};
  ["HR", "BP", "MAP", "RR", "SpO2", "Temp"].forEach((k) => {
    if (form[k]) vitals[k] = form[k];
  });
  return {
    title: form.title,
    category: form.category,
    age: Number(form.age) || 50,
    gender: form.gender,
    context: form.context,
    vitals,
    critical_data: form.critical_data
      ? form.critical_data.split("\n").map((s) => s.trim()).filter(Boolean)
      : [],
  };
}

// ---------- Login Screen ----------
function AdminLogin({ onAuthed }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setErr("");
    try {
      await adminLogin(password);
      onAuthed();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 429) {
        setErr(e.response.data?.detail || "Too many failed attempts. Try again later.");
      } else if (code === 401) {
        setErr("Incorrect password.");
      } else {
        setErr(e?.response?.data?.detail || "Login failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f1eee7] grid place-items-center px-6">
      <Toaster position="top-right" />
      <div className="w-full max-w-sm">
        <button
          onClick={() => navigate("/")}
          data-testid="login-back-btn"
          className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#6b6258] hover:text-[#6f2a30] transition-colors mb-6"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <div className="bg-[#fbf8f2] border border-[#e4ded2] rounded-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-9 w-9 bg-[#6f2a30] text-[#f1eee7] grid place-items-center rounded-sm">
              <Lock className="h-4 w-4" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6b6258]">
                Project RN · Admin
              </div>
              <div className="font-semibold tracking-tight text-[#1a1612]">
                Restricted access
              </div>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                data-testid="admin-password-input"
                className="w-full px-3 py-2.5 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm text-[#1a1612] focus:outline-none focus:border-[#6f2a30] focus:bg-[#fbf8f2]"
              />
            </div>
            {err && (
              <div data-testid="login-error" className="text-xs text-[#6f2a30] font-medium">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !password}
              data-testid="admin-login-submit"
              className="w-full bg-[#6f2a30] text-[#f1eee7] py-2.5 rounded-sm hover:bg-[#582227] transition-colors disabled:opacity-50 text-sm uppercase tracking-widest font-medium"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------- Change Password Modal ----------
function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await adminChangePassword(current, next);
      toast.success("Password updated");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#1a1612]/50 z-50 grid place-items-center px-6" onClick={onClose}>
      <div
        className="bg-[#fbf8f2] border border-[#e4ded2] rounded-sm p-7 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#6f2a30]" strokeWidth={1.8} />
            <span className="font-semibold text-[#1a1612] tracking-tight">Change Password</span>
          </div>
          <button onClick={onClose} data-testid="close-change-pw" className="text-[#6b6258] hover:text-[#1a1612]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="cp-current"
            className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
          />
          <input
            type="password"
            placeholder="New password (min 6 chars)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="cp-new"
            className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="cp-confirm"
            className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
          />
          <button
            type="submit"
            disabled={loading || !current || !next || !confirm}
            data-testid="cp-submit"
            className="w-full bg-[#6f2a30] text-[#f1eee7] py-2.5 rounded-sm hover:bg-[#582227] transition-colors disabled:opacity-50 text-sm uppercase tracking-widest font-medium"
          >
            {loading ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------- References Panel ----------
function ReferencesPanel({ onSignOut }) {
  const confirm = useConfirm();
  const [refs, setRefs] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("drug");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", category: "drug", description: "" });
  const [reordering, setReordering] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const refresh = async () => {
    try {
      setRefs(await listReferences());
    } catch {
      toast.error("Failed to load references");
    }
  };
  useEffect(() => { refresh(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files supported");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("PDF must be under 15 MB");
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ""));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title) {
      toast.error("File and title required");
      return;
    }
    setSubmitting(true);
    try {
      await uploadReference(file, title, category, description);
      toast.success("Reference uploaded");
      setFile(null);
      setTitle("");
      setDescription("");
      setCategory("drug");
      refresh();
    } catch (e) {
      if (e?.response?.status === 401) {
        onSignOut();
      } else {
        toast.error(e?.response?.data?.detail || "Upload failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (r) => {
    const ok = await confirm({
      title: `Delete "${r.title}"?`,
      message: "This reference sheet will be permanently removed from the library.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteReference(r.id);
      toast.success("Reference deleted");
      refresh();
    } catch (e) {
      if (e?.response?.status === 401) onSignOut();
      else toast.error("Failed to delete");
    }
  };

  const beginEdit = (r) => {
    setEditingId(r.id);
    setEditForm({ title: r.title, category: r.category, description: r.description || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", category: "drug", description: "" });
  };

  const saveEdit = async (r) => {
    const t = editForm.title.trim();
    if (!t) { toast.error("Title required"); return; }
    try {
      await updateReference(r.id, {
        title: t,
        category: editForm.category,
        description: editForm.description.trim(),
      });
      toast.success("Saved");
      cancelEdit();
      refresh();
    } catch (e) {
      if (e?.response?.status === 401) onSignOut();
      else toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const move = async (index, delta) => {
    const newIndex = index + delta;
    if (newIndex < 0 || newIndex >= refs.length) return;
    if (reordering) return;
    await persistOrder(swap(refs, index, newIndex));
  };

  const swap = (arr, i, j) => {
    const next = arr.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  };

  const persistOrder = async (nextRefs) => {
    setReordering(true);
    setRefs(nextRefs); // optimistic
    try {
      await reorderReferences(nextRefs.map((r, i) => ({ id: r.id, sort_order: i })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
      refresh();
    } finally {
      setReordering(false);
    }
  };

  // Native HTML5 drag-and-drop handlers
  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Some browsers require a data payload
    try { e.dataTransfer.setData("text/plain", id); } catch (_) { /* Safari */ }
  };
  const onDragOverRow = (id) => (e) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== id) setDragOverId(id);
  };
  const onDragLeaveRow = () => setDragOverId(null);
  const onDropRow = (id) => (e) => {
    e.preventDefault();
    const from = refs.findIndex((r) => r.id === dragId);
    const to = refs.findIndex((r) => r.id === id);
    setDragId(null);
    setDragOverId(null);
    if (from < 0 || to < 0 || from === to) return;
    const next = refs.slice();
    const [moved] = next.splice(from, 1);
    // If we dragged downwards, the target index shifts by 1 after the splice above.
    const insertAt = to > from ? to - 1 : to;
    next.splice(insertAt, 0, moved);
    persistOrder(next);
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };

  const sortAlpha = async () => {
    const ok = await confirm({
      title: "Re-alphabetize library?",
      message: "This will sort the entire list A→Z by title and overwrite any custom drag ordering.",
      confirmLabel: "Sort A→Z",
    });
    if (!ok) return;
    try {
      await sortReferencesAlpha();
      toast.success("Library sorted alphabetically");
      refresh();
    } catch (e) {
      if (e?.response?.status === 401) onSignOut();
      else toast.error(e?.response?.data?.detail || "Sort failed");
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 py-10">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6258]">Library</div>
          <h2 className="text-2xl font-black tracking-tight text-[#1a1612]">Reference Sheets</h2>
          <p className="text-[11px] text-[#9c8f7e] mt-1">
            Click a sheet to preview inline. Drag to reorder. New uploads slot into alphabetical position.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-[#6b6258]" data-testid="ref-count">
            {refs.length} sheets
          </span>
          <button
            onClick={sortAlpha}
            data-testid="ref-sort-alpha"
            title="Re-alphabetize the whole library"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#d8ceb8] text-[#1a1612] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#f1eee7] transition-colors"
          >
            A→Z
          </button>
          <button
            onClick={() => setUploadOpen((v) => !v)}
            data-testid="ref-upload-toggle"
            className="flex items-center gap-1.5 px-3 py-2 bg-[#6f2a30] text-[#f1eee7] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#582227] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            {uploadOpen ? "Close" : "Upload"}
          </button>
        </div>
      </div>

      {/* Collapsible upload form */}
      {uploadOpen && (
        <form
          onSubmit={submit}
          data-testid="ref-upload-form"
          className="bg-[#fbf8f2] border border-[#e4ded2] rounded-sm p-5 space-y-4 mb-6"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">PDF File</label>
              <label className="flex items-center gap-2 px-3 py-3 border border-dashed border-[#c8bfa9] rounded-sm bg-[#f1eee7] cursor-pointer hover:border-[#6f2a30] transition-colors">
                <Upload className="h-4 w-4 text-[#6b6258]" />
                <span className="text-xs text-[#6b6258] truncate">{file ? file.name : "Choose PDF (max 15 MB)"}</span>
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid="ref-file-input" />
              </label>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="ref-cat-select"
                className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
              >
                <option value="drug">Drug</option>
                <option value="device">Device</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Norepinephrine Quick Reference"
              data-testid="ref-title-input"
              className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30] focus:bg-[#fbf8f2]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short blurb — appears in the library card"
              rows={2}
              data-testid="ref-desc-input"
              className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30] focus:bg-[#fbf8f2] resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !file || !title}
            data-testid="ref-submit-btn"
            className="w-full bg-[#6f2a30] text-[#f1eee7] py-2.5 rounded-sm hover:bg-[#582227] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm uppercase tracking-widest font-medium"
          >
            <FilePlus className="h-4 w-4" />
            {submitting ? "Uploading…" : "Upload Sheet"}
          </button>
          <p className="text-[11px] text-[#9c8f7e] leading-relaxed">
            PDFs are stored privately. Text is extracted automatically so ICU Brain can reference your sheets when nurses ask about a drug or device.
          </p>
        </form>
      )}

      {/* List */}
      <div className="space-y-2">
        {refs.map((r, i) => {
          const isPreview = previewId === r.id;
          const isDragOver = dragOverId === r.id && dragId && dragId !== r.id;
          const isDragging = dragId === r.id;
          return (
            <div
              key={r.id}
              onDragOver={onDragOverRow(r.id)}
              onDragLeave={onDragLeaveRow}
              onDrop={onDropRow(r.id)}
              data-testid={`ref-item-${r.id}`}
              className={`bg-[#fbf8f2] border rounded-sm transition-colors ${
                isDragOver ? "border-[#6f2a30] border-2" : "border-[#e4ded2] hover:border-[#c8bfa9]"
              } ${isDragging ? "opacity-40" : ""}`}
            >
              {editingId === r.id ? (
                <div className="p-4 space-y-3">
                  <input
                    value={editForm.title}
                    onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                    data-testid={`ref-edit-title-${r.id}`}
                    className="w-full px-3 py-2 bg-white border border-[#d8ceb8] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
                    placeholder="Title"
                  />
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                    data-testid={`ref-edit-cat-${r.id}`}
                    className="w-full px-3 py-2 bg-white border border-[#d8ceb8] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
                  >
                    <option value="drug">Drug</option>
                    <option value="device">Device</option>
                  </select>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                    data-testid={`ref-edit-desc-${r.id}`}
                    rows={2}
                    placeholder="Description (optional)"
                    className="w-full px-3 py-2 bg-white border border-[#d8ceb8] rounded-sm text-xs focus:outline-none focus:border-[#6f2a30] resize-none"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => saveEdit(r)}
                      data-testid={`ref-edit-save-${r.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6f2a30] text-[#f1eee7] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#582227]"
                    >
                      <Save className="h-3.5 w-3.5" /> Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      data-testid={`ref-edit-cancel-${r.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-[#d8ceb8] text-[#1a1612] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#f1eee7]"
                    >
                      <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 px-3 py-3">
                    {/* Drag handle */}
                    <div
                      draggable
                      onDragStart={onDragStart(r.id)}
                      onDragEnd={onDragEnd}
                      title="Drag to reorder"
                      data-testid={`ref-drag-${r.id}`}
                      className="p-1 text-[#9c8f7e] hover:text-[#1a1612] cursor-grab active:cursor-grabbing"
                    >
                      <GripVertical className="h-4 w-4" strokeWidth={1.8} />
                    </div>
                    {/* Up / Down arrows (still available for touch users who don't drag) */}
                    <div className="flex flex-col items-center gap-0 shrink-0">
                      <button
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || reordering}
                        title="Move up"
                        data-testid={`ref-up-${r.id}`}
                        className="p-0.5 text-[#6b6258] hover:text-[#1a1612] rounded-sm disabled:opacity-30"
                      >
                        <ArrowUp className="h-3 w-3" strokeWidth={2} />
                      </button>
                      <span className="font-mono text-[9px] text-[#9c8f7e] tabular-nums leading-none">{i + 1}</span>
                      <button
                        onClick={() => move(i, 1)}
                        disabled={i === refs.length - 1 || reordering}
                        title="Move down"
                        data-testid={`ref-down-${r.id}`}
                        className="p-0.5 text-[#6b6258] hover:text-[#1a1612] rounded-sm disabled:opacity-30"
                      >
                        <ArrowDown className="h-3 w-3" strokeWidth={2} />
                      </button>
                    </div>
                    {/* Row body — clickable to toggle preview */}
                    <button
                      onClick={() => setPreviewId(isPreview ? null : r.id)}
                      data-testid={`ref-toggle-${r.id}`}
                      className="flex-1 min-w-0 text-left group py-1"
                    >
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-[#1a1612] text-sm tracking-tight truncate group-hover:text-[#6f2a30] transition-colors">{r.title}</div>
                        {isPreview
                          ? <ChevronUp className="h-3.5 w-3.5 text-[#9c8f7e] shrink-0" />
                          : <ChevronDown className="h-3.5 w-3.5 text-[#9c8f7e] shrink-0" />}
                      </div>
                      <div className="font-mono text-xs text-[#6b6258] mt-0.5">
                        {r.category} · {(r.size / 1024).toFixed(0)} KB
                      </div>
                      {r.description && !isPreview && (
                        <p className="text-xs text-[#3a322b] mt-1 leading-relaxed line-clamp-1">{r.description}</p>
                      )}
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => beginEdit(r)}
                        title="Edit"
                        data-testid={`ref-edit-${r.id}`}
                        className="p-1.5 text-[#6b6258] hover:text-[#6f2a30] hover:bg-[#f1eee7] rounded-sm transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
                      </button>
                      <button
                        onClick={() => onDelete(r)}
                        title="Delete"
                        data-testid={`ref-delete-${r.id}`}
                        className="p-1.5 text-[#6b6258] hover:text-[#d62828] hover:bg-[#f1eee7] rounded-sm transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                      </button>
                    </div>
                  </div>
                  {/* Inline PDF preview */}
                  {isPreview && (
                    <div
                      className="border-t border-[#e4ded2] bg-white"
                      data-testid={`ref-preview-${r.id}`}
                      style={{ height: "60vh" }}
                    >
                      <div className="h-full flex flex-col">
                        <PdfViewer
                          key={r.id}
                          fileUrl={referenceFileUrl(r.id)}
                          filename={r.original_filename}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
        {refs.length === 0 && <div className="text-[#6b6258] text-sm">No reference sheets uploaded yet.</div>}
      </div>
    </main>
  );
}

// ---------- Analytics Panel ----------
const FEATURE_LABEL = {
  welcome: "Welcome Page",
  simulation: "Simulation (all activities)",
  references: "References Library",
  code_scribe: "Code Scribe",
  admin: "Admin Panel",
};

function AnalyticsPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = getAdminToken();
      const res = await getAnalyticsSummary(token);
      setData(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  return (
    <main className="max-w-5xl mx-auto px-6 py-10" data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6258] mb-1">Usage Analytics</div>
          <h2 className="text-2xl font-black tracking-tight text-[#1a1612]">Feature usage & time spent</h2>
          <p className="text-xs text-[#6b6258] mt-1">
            Anonymous per-device tracking with IP-based dedup. Events auto-purge after {data?.totals?.retention_days ?? 90} days.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="analytics-refresh"
          className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest text-[#6b6258] hover:text-[#1a1612] border border-[#e4ded2] rounded-sm hover:bg-[#f1eee7]"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {loading && <div className="font-mono text-sm text-[#6b6258]">Loading analytics…</div>}
      {err && <div className="text-sm text-[#6f2a30]" data-testid="analytics-error">{err}</div>}

      {data && !loading && !err && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <TotalCard label="Total Feature Opens" value={data.totals.total_opens_all} testid="tot-opens" />
            <TotalCard label="Total Minutes Used" value={data.totals.total_minutes_all} testid="tot-minutes" />
            <TotalCard label="Unique Device-Days" value={data.totals.unique_visits_est} testid="tot-uniq" />
          </div>

          {/* Feature table */}
          <div className="bg-[#fbf8f2] border border-[#e4ded2] rounded-sm overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-widest text-[#6b6258] border-b border-[#e4ded2] bg-[#f1eee7]">
              <div className="col-span-4">Feature</div>
              <div className="col-span-2 text-right">All-time</div>
              <div className="col-span-1 text-right">7d</div>
              <div className="col-span-1 text-right">30d</div>
              <div className="col-span-2 text-right">Total min</div>
              <div className="col-span-2 text-right">Avg session</div>
            </div>
            {data.features.map((f) => (
              <div
                key={f.feature}
                data-testid={`row-${f.feature}`}
                className="grid grid-cols-12 px-4 py-3 text-sm text-[#1a1612] border-b last:border-b-0 border-[#e4ded2] hover:bg-[#f1eee7]"
              >
                <div className="col-span-4 font-semibold">{FEATURE_LABEL[f.feature] || f.feature}</div>
                <div className="col-span-2 text-right font-mono tabular-nums">{f.total_opens}</div>
                <div className="col-span-1 text-right font-mono tabular-nums text-[#6b6258]">{f.opens_7d}</div>
                <div className="col-span-1 text-right font-mono tabular-nums text-[#6b6258]">{f.opens_30d}</div>
                <div className="col-span-2 text-right font-mono tabular-nums">{f.total_minutes}</div>
                <div className="col-span-2 text-right font-mono tabular-nums text-[#6b6258]">
                  {f.avg_session_minutes || "—"}
                </div>
              </div>
            ))}
            {data.features.every((f) => f.total_opens === 0) && (
              <div className="px-4 py-6 text-center text-sm text-[#6b6258]">
                No usage recorded yet. Open the app in another tab (or wait for nurses to use it) — data appears here in seconds.
              </div>
            )}
          </div>

          <p className="text-[10px] uppercase tracking-widest text-[#6b6258] mt-4">
            Last updated {new Date(data.totals.generated_at).toLocaleString()}
          </p>
        </>
      )}
    </main>
  );
}

function TotalCard({ label, value, testid }) {
  return (
    <div className="bg-[#fbf8f2] border border-[#e4ded2] rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#6b6258]">{label}</div>
      <div className="text-3xl font-black tracking-tight text-[#1a1612] font-mono tabular-nums mt-1">{value}</div>
    </div>
  );
}

// ---------- Main Admin (authed) ----------
function AdminPanel({ onSignOut }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("scenarios"); // "scenarios" | "references" | "analytics"
  const [scenarios, setScenarios] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [pdfName, setPdfName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);
  const confirm = useConfirm();

  const refresh = async () => {
    try {
      const data = await listScenarios();
      setScenarios(data);
    } catch {
      toast.error("Failed to load scenarios");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPdfName("");
  };

  const beginEdit = (s) => {
    setEditingId(s.id);
    setForm(scenarioToForm(s));
    setPdfName("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.context) {
      toast.error("Title and context are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = formToPayload(form);
      if (editingId) {
        await updateScenario(editingId, payload);
        toast.success("Scenario updated");
      } else {
        await createScenario(payload);
        toast.success("Scenario added");
      }
      cancelEdit();
      refresh();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Signing out…");
        onSignOut();
      } else {
        toast.error("Failed to save scenario");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (s) => {
    const ok = await confirm({
      title: `Delete "${s.title}"?`,
      message: "This scenario will be permanently removed. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteScenario(s.id);
      toast.success("Scenario deleted");
      if (editingId === s.id) cancelEdit();
      refresh();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Signing out…");
        onSignOut();
      } else {
        toast.error("Failed to delete");
      }
    }
  };

  const onPdf = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setPdfName(f.name);
      toast.info("PDF noted (reference only)");
    }
  };

  return (
    <div className="min-h-screen bg-[#f1eee7]">
      <Toaster position="top-right" />
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}

      <header className="border-b border-[#e4ded2] bg-[#fbf8f2]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              data-testid="admin-back-btn"
              className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#6b6258] hover:text-[#6f2a30] transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            <div className="h-5 w-px bg-[#e4ded2]" />
            <div className="flex items-center gap-2.5">
              <img
                src="/project-rn-logo-horizontal.png"
                alt="Project RN"
                data-testid="app-logo"
                className="h-8 w-auto"
              />
              <div className="h-5 w-px bg-[#e4ded2]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6b6258]">
                  Admin
                </div>
                <div className="font-semibold tracking-tight text-[#1a1612]">
                  Scenario Library
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowChangePw(true)}
              data-testid="open-change-pw"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e4ded2] text-[#1a1612] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#6f2a30] hover:text-[#f1eee7] hover:border-[#6f2a30] transition-colors"
            >
              <KeyRound className="h-3.5 w-3.5" strokeWidth={1.8} />
              <span className="hidden sm:inline">Change Password</span>
            </button>
            <button
              onClick={onSignOut}
              data-testid="admin-signout"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6f2a30] text-[#f1eee7] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#582227] transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.8} />
              Sign out
            </button>
          </div>
        </div>
        {/* Tab switcher */}
        <div className="max-w-6xl mx-auto px-6 pb-3 flex items-center gap-1">
          <button
            onClick={() => setTab("scenarios")}
            data-testid="tab-scenarios"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-medium rounded-sm transition-colors ${
              tab === "scenarios"
                ? "bg-[#1a1612] text-[#f1eee7]"
                : "text-[#6b6258] hover:bg-[#f1eee7]"
            }`}
          >
            <Files className="h-3.5 w-3.5" strokeWidth={1.8} />
            Scenarios
          </button>
          <button
            onClick={() => setTab("references")}
            data-testid="tab-references"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-medium rounded-sm transition-colors ${
              tab === "references"
                ? "bg-[#1a1612] text-[#f1eee7]"
                : "text-[#6b6258] hover:bg-[#f1eee7]"
            }`}
          >
            <FileText className="h-3.5 w-3.5" strokeWidth={1.8} />
            References
          </button>
          <button
            onClick={() => setTab("analytics")}
            data-testid="tab-analytics"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest font-medium rounded-sm transition-colors ${
              tab === "analytics"
                ? "bg-[#1a1612] text-[#f1eee7]"
                : "text-[#6b6258] hover:bg-[#f1eee7]"
            }`}
          >
            <BarChart3 className="h-3.5 w-3.5" strokeWidth={1.8} />
            Analytics
          </button>
        </div>
      </header>

      {tab === "references" ? (
        <ReferencesPanel onSignOut={onSignOut} />
      ) : tab === "analytics" ? (
        <AnalyticsPanel />
      ) : (
        <main className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Form */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6258]">
              {editingId ? "Edit Scenario" : "Add Scenario"}
            </div>
            {editingId && (
              <button
                onClick={cancelEdit}
                data-testid="cancel-edit-btn"
                className="text-[10px] uppercase tracking-widest text-[#6b6258] hover:text-[#6f2a30]"
              >
                Cancel edit
              </button>
            )}
          </div>
          <form onSubmit={submit} className="bg-[#fbf8f2] border border-[#e4ded2] rounded-sm p-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">Title</label>
              <input
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                data-testid="admin-title-input"
                className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30] focus:bg-[#fbf8f2]"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  data-testid="admin-category-select"
                  className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">Age</label>
                <input
                  type="number"
                  value={form.age}
                  onChange={(e) => set("age", e.target.value)}
                  data-testid="admin-age-input"
                  className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm font-mono focus:outline-none focus:border-[#6f2a30]"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">Gender</label>
                <select
                  value={form.gender}
                  onChange={(e) => set("gender", e.target.value)}
                  data-testid="admin-gender-select"
                  className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
                >
                  <option>M</option>
                  <option>F</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">
                Context (brief, 1–2 lines)
              </label>
              <textarea
                value={form.context}
                onChange={(e) => set("context", e.target.value)}
                data-testid="admin-context-input"
                rows={2}
                className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30] focus:bg-[#fbf8f2] resize-none"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">Vitals</label>
              <div className="grid grid-cols-3 gap-2">
                {["HR", "BP", "MAP", "RR", "SpO2", "Temp"].map((k) => (
                  <input
                    key={k}
                    placeholder={k}
                    value={form[k]}
                    onChange={(e) => set(k, e.target.value)}
                    data-testid={`admin-vital-${k.toLowerCase()}`}
                    className="px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm font-mono focus:outline-none focus:border-[#6f2a30] focus:bg-[#fbf8f2]"
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">
                Critical Data (one per line)
              </label>
              <textarea
                value={form.critical_data}
                onChange={(e) => set("critical_data", e.target.value)}
                data-testid="admin-critical-input"
                rows={3}
                className="w-full px-3 py-2 bg-[#f1eee7] border border-[#e4ded2] rounded-sm text-sm font-mono focus:outline-none focus:border-[#6f2a30] focus:bg-[#fbf8f2] resize-none"
              />
            </div>

            {!editingId && (
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6b6258] block mb-1.5">
                  Reference PDF (optional)
                </label>
                <label className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-[#c8bfa9] rounded-sm bg-[#f1eee7] cursor-pointer hover:border-[#6f2a30] transition-colors">
                  <Upload className="h-4 w-4 text-[#6b6258]" />
                  <span className="text-xs text-[#6b6258]">{pdfName || "Attach PDF (reference only)"}</span>
                  <input type="file" accept="application/pdf" className="hidden" onChange={onPdf} data-testid="admin-pdf-input" />
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-submit-btn"
              className="w-full bg-[#6f2a30] text-[#f1eee7] py-2.5 rounded-sm hover:bg-[#582227] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm uppercase tracking-widest font-medium"
            >
              {editingId ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {submitting ? (editingId ? "Saving…" : "Adding…") : editingId ? "Save Changes" : "Add Scenario"}
            </button>
          </form>
        </section>

        {/* List */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6258]">Library</div>
            <span className="font-mono text-xs text-[#6b6258]" data-testid="scenario-count">
              {scenarios.length} cases
            </span>
          </div>
          <div className="space-y-2 max-h-[80vh] overflow-y-auto scroll-clean pr-2">
            {scenarios.map((s) => {
              const isEditing = editingId === s.id;
              return (
                <div
                  key={s.id}
                  className={`bg-[#fbf8f2] border rounded-sm p-4 transition-colors ${
                    isEditing ? "border-[#6f2a30]" : "border-[#e4ded2] hover:border-[#6f2a30]"
                  }`}
                  data-testid={`scenario-item-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#1a1612] text-sm tracking-tight truncate">
                        {s.title}
                      </div>
                      <div className="font-mono text-xs text-[#6b6258] mt-0.5">
                        {s.age} {s.gender} · {s.category}
                        {s.custom && " · custom"}
                        {isEditing && " · editing"}
                      </div>
                      <p className="text-xs text-[#3a322b] mt-2 leading-relaxed line-clamp-2">{s.context}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => beginEdit(s)}
                        title="Edit"
                        data-testid={`edit-${s.id}`}
                        className="p-1.5 text-[#6b6258] hover:text-[#6f2a30] hover:bg-[#f1eee7] rounded-sm transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
                      </button>
                      <button
                        onClick={() => onDelete(s)}
                        title="Delete"
                        data-testid={`delete-${s.id}`}
                        className="p-1.5 text-[#6b6258] hover:text-[#d62828] hover:bg-[#f1eee7] rounded-sm transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {scenarios.length === 0 && <div className="text-[#6b6258] text-sm">No scenarios yet.</div>}
          </div>
        </section>
      </main>
      )}
    </div>
  );
}

// ---------- Top-level Admin component ----------
export default function Admin() {
  useFeatureTimer("admin");
  const [authed, setAuthed] = useState(null); // null = checking, true/false = result

  useEffect(() => {
    const token = getAdminToken();
    if (!token) {
      setAuthed(false);
      return;
    }
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => {
        clearAdminToken();
        setAuthed(false);
      });
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#f1eee7] grid place-items-center">
        <span className="font-mono text-sm text-[#6b6258]">Checking session…</span>
      </div>
    );
  }

  if (!authed) {
    return <AdminLogin onAuthed={() => setAuthed(true)} />;
  }

  return (
    <AdminPanel
      onSignOut={() => {
        clearAdminToken();
        setAuthed(false);
      }}
    />
  );
}
